import { useState } from "react";
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { errorMessageOf } from "../../api/scholarships";
import { ConfirmModal, RowAction, StatusBadge, StatusTabs, Table, Tag, Textarea } from "../ui";
import { BURSARIES_KEY, nameOf, useDebouncedSearch } from "./shared";

const PAGE_SIZE = 100;
const MAX_REASON = 500;

export const LOAN_REQUESTS_KEY = [BURSARIES_KEY, "requests"];

const STATUSES = ["PENDING", "GRANTED", "REJECTED"];

const STATUS_TABS = [
  { value: "", label: "All requests" },
  { value: "PENDING", label: "Pending" },
  { value: "GRANTED", label: "Granted" },
  { value: "REJECTED", label: "Rejected" },
];

const DECIDERS = { HLI_ADMIN: "Institution", ADMIN: "HEC Admin", SELECTION: "Selection" };

const scoreColor = (score) => (score >= 80 ? "green" : score >= 60 ? "yellow" : "red");

// A request is decided by hand while pending or after a decision by hand; the selection's own decisions stay
const decidable = (r) => r.request.decidedBy !== "SELECTION";

/**
 * The bursary requests students made, one page at a time from `list(params)`, searched on the server.
 * Pending requests, and those decided by hand, can be granted or rejected through `decide(studentId, body)`;
 * a rejection may carry a reason the student sees. `counts` (by status) shows on the status tabs; without
 * it each status is counted from `list` itself. `onDecided` runs after each decision.
 */
export default function LoanRequestsTable({ list, params, decide: decideRequest, exportName, counts, onDecided }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const { search, setSearch, debounced } = useDebouncedSearch(() => setPage(0));
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const { data, isPending } = useQuery({
    queryKey: [...LOAN_REQUESTS_KEY, params, status, page, debounced],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      try {
        return (await list({ ...params, page, size: PAGE_SIZE, ...(status && { status }), ...(debounced && { q: debounced }) })).data;
      } catch (error) {
        console.error("Failed to fetch bursary requests:", error);
        toast.error(errorMessageOf(error, "Failed to fetch the requests."), { id: "loan-requests-error" });
        throw error;
      }
    },
  });

  // One row per status is enough to read its total; skipped when the caller already knows the counts
  const statusTotals = useQueries({
    queries: STATUSES.map((value) => ({
      queryKey: [...LOAN_REQUESTS_KEY, params, "count", value],
      enabled: !counts,
      queryFn: async () => (await list({ ...params, status: value, page: 0, size: 1 })).data.total,
    })),
  });
  const tabCounts = counts ?? (statusTotals.every((q) => q.isSuccess) ? Object.fromEntries(STATUSES.map((value, i) => [value, statusTotals[i].data])) : null);

  const decide = useMutation({
    mutationFn: ({ studentId, decision, reason: why }) => decideRequest(studentId, { decision, ...(why?.trim() && { reason: why.trim() }) }),
    onSuccess: (_, { decision }) => {
      toast.success(`Request ${decision === "GRANTED" ? "granted" : "rejected"}.`, { autoClose: 4000 });
      setRejecting(null);
      queryClient.invalidateQueries({ queryKey: [BURSARIES_KEY] });
      onDecided?.();
    },
    onError: (error) => {
      console.error("Failed to decide the request:", error);
      toast.error(errorMessageOf(error, "Failed to update the request."));
    },
  });
  const decidingId = decide.isPending ? decide.variables?.studentId : null;

  const openReject = (loan) => {
    setReason("");
    setRejecting(loan);
  };

  const rows = (data?.items ?? []).map((r, i) => ({ ...r, id: r.student.id, index: page * PAGE_SIZE + i + 1 }));
  const totalElements = data?.total ?? rows.length;

  const columns = [
    { key: "index", label: "#", className: "text-slate-500" },
    { key: "name", label: "Name", accessor: (r) => nameOf(r.student), className: "font-semibold text-slate-900" },
    { key: "regNumber", label: "Reg number", accessor: (r) => r.student.registrationNumber },
    { key: "gender", label: "Gender", accessor: (r) => r.student.gender },
    { key: "studyArea", label: "Study area", accessor: (r) => r.request.studyArea ?? "—" },
    { key: "totalScore", label: "Score", accessor: (r) => r.request.totalScore, render: (r) => (r.request.totalScore != null ? <Tag color={scoreColor(r.request.totalScore)}>{r.request.totalScore}</Tag> : "—") },
    { key: "status", label: "Status", accessor: (r) => r.request.status, render: (r) => <StatusBadge status={r.request.status} /> },
    { key: "decidedBy", label: "Decided by", accessor: (r) => DECIDERS[r.request.decidedBy] ?? "—" },
    { key: "reason", label: "Reason", accessor: (r) => r.request.decisionReason ?? "—", className: "max-w-xs text-slate-600" },
    {
      key: "actions",
      label: "",
      exportable: false,
      render: (r) =>
        decidable(r) && (
          <div className="flex justify-end gap-1">
            {r.request.status !== "GRANTED" && <RowAction label="Grant" Icon={CheckIcon} variant="soft" loading={decidingId === r.id} onClick={() => decide.mutate({ studentId: r.id, decision: "GRANTED" })} />}
            {r.request.status !== "REJECTED" && <RowAction label="Reject" Icon={XMarkIcon} variant="danger-soft" loading={decidingId === r.id} onClick={() => openReject(r)} />}
          </div>
        ),
    },
  ];

  return (
    <>
      <StatusTabs
        tabs={tabCounts ? STATUS_TABS.map((t) => ({ ...t, count: t.value ? tabCounts[t.value] ?? 0 : undefined })) : STATUS_TABS}
        value={status}
        onChange={(value) => {
          setStatus(value);
          setPage(0);
        }}
      />
      <Table
        columns={columns}
        data={rows}
        isLoading={isPending}
        searchPlaceholder="Search by name, reg number, national ID, index number, email or phone"
        searchValue={search}
        onSearchChange={setSearch}
        exportName={exportName}
        emptyTitle={debounced ? "No loan requests match your search" : "No loan requests"}
        pagination={{ page, totalPages: Math.ceil(totalElements / PAGE_SIZE), totalElements, pageSize: PAGE_SIZE, onPageChange: setPage }}
      />
      <ConfirmModal
        open={Boolean(rejecting)}
        onClose={() => setRejecting(null)}
        onConfirm={() => decide.mutate({ studentId: rejecting.id, decision: "REJECTED", reason })}
        title={`Reject ${rejecting ? nameOf(rejecting.student) : ""}'s request?`}
        description="The student sees the decision straight away."
        confirmLabel="Reject"
        destructive
        loading={decide.isPending}
      >
        <Textarea label="Reason (optional)" info="Shown to the student" rows={3} maxLength={MAX_REASON} value={reason} onChange={(e) => setReason(e.target.value)} />
      </ConfirmModal>
    </>
  );
}
